import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Visibility,
  VisibilityOff,
  MailOutline,
  LockOutlined,
  InsightsOutlined,
  BadgeOutlined,
  HowToVoteOutlined,
  ShieldOutlined,
} from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useUSAJILI, USAJILIProvider } from '@hooks/useUSAJILI';
import dayjs from 'dayjs';
import { useNow } from '@components/usajili/USAJILIHeaderStatus';
import { OtpCodeStep } from '@components/auth/OtpCodeStep';
import type { OtpChallenge } from '@services/api/client';
import {
  USAJILIThemeProvider,
  USAJILIColorModeToggle,
  useUSAJILIColorMode,
} from '@theme/USAJILIThemeProvider';

type FormValues = { email: string; password: string };

/** What a sign-in page built on this one shows and where it goes. The
 *  accounts are the same everywhere: SDICS admin login + a usajili_user role. */
export interface LoginBrand {
  title: string;
  tagline: string;
  features: Array<{ icon: React.ReactElement; title: string; text: string }>;
  /** Route to open after signing in. */
  home: string;
}

const USAJILI_BRAND: LoginBrand = {
  title: 'RRI USAJILI',
  tagline: 'Track new IDs, ID replacements and voter registration across the counties — in one dashboard.',
  features: [
    { icon: <InsightsOutlined />, title: 'Daily county returns', text: 'Registration progress from each county, day by day.' },
    { icon: <BadgeOutlined />, title: 'New IDs & replacements', text: 'Totals to date with county and date filters.' },
    { icon: <HowToVoteOutlined />, title: 'Voter registration trends', text: 'Charts and pivot tables for quick review.' },
  ],
  home: '/usajili/dashboard',
};

/** Thin black–red–green stripe in the colours of the Kenyan flag. */
export const FlagStripe: React.FC<{ height?: number }> = ({ height = 4 }) => (
  <Box sx={{ display: 'flex', height, width: '100%' }}>
    <Box sx={{ flex: 1, bgcolor: '#000000' }} />
    <Box sx={{ flex: 1, bgcolor: '#BB0000' }} />
    <Box sx={{ flex: 1, bgcolor: '#006600' }} />
  </Box>
);

export const CoatOfArms: React.FC<{ size: number }> = ({ size }) => (
  <Box
    component="img"
    src="/coat-of-arms-sm.png"
    alt="Kenya Coat of Arms"
    sx={{
      width: size,
      height: size,
      borderRadius: '50%',
      bgcolor: '#fff',
      p: `${Math.round(size * 0.08)}px`,
      objectFit: 'contain',
      boxShadow: '0 4px 18px rgba(0,0,0,0.25)',
    }}
  />
);

/** Left-hand brand panel, shown on medium screens and wider. */
export const BrandPanel: React.FC<{ brand: LoginBrand }> = ({ brand }) => {
  const { mode } = useUSAJILIColorMode();
  const now = useNow();
  const bg =
    mode === 'dark'
      ? 'linear-gradient(145deg, #0b3d2e 0%, #0f1f1c 45%, #0f1419 100%)'
      : 'linear-gradient(145deg, #00875A 0%, #006644 100%)';

  return (
    <Box
      sx={{
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
        background: bg,
        color: '#FFFFFF',
        px: 7,
        pt: 6,
      }}
    >
      {/* soft decorative rings */}
      <Box sx={{ position: 'absolute', width: 520, height: 520, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)', right: -220, top: -160 }} />
      <Box sx={{ position: 'absolute', width: 360, height: 360, borderRadius: '50%', border: '1px solid rgba(255,255,255,0.08)', right: -120, top: -60 }} />

      <Stack direction="row" spacing={2} alignItems="center" sx={{ position: 'relative' }}>
        <CoatOfArms size={64} />
        <Box>
          <Typography sx={{ fontSize: '0.75rem', letterSpacing: 2, fontWeight: 500, opacity: 0.85 }}>
            REPUBLIC OF KENYA
          </Typography>
          <Typography sx={{ fontSize: '0.95rem', fontWeight: 500, opacity: 0.95 }}>
            Registration Drive Monitoring
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ position: 'relative', maxWidth: 520 }}>
        <Typography sx={{ fontSize: { md: '2.6rem', lg: '3.1rem' }, fontWeight: 700, lineHeight: 1.1, letterSpacing: 1 }}>
          {brand.title}
        </Typography>
        <Typography sx={{ mt: 1.5, fontSize: '1.05rem', lineHeight: 1.6, opacity: 0.9 }}>
          {brand.tagline}
        </Typography>

        <Stack spacing={2.25} sx={{ mt: 4 }}>
          {brand.features.map((f) => (
            <Stack key={f.title} direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  flexShrink: 0,
                  borderRadius: 2,
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.18)',
                }}
              >
                {f.icon}
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{f.title}</Typography>
                <Typography sx={{ fontSize: '0.85rem', opacity: 0.85 }}>{f.text}</Typography>
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>

      <Box sx={{ position: 'relative', mx: -7 }}>
        <Typography sx={{ px: 7, pb: 2, fontSize: '1rem', fontWeight: 500, opacity: 0.9 }}>
          {dayjs(now).format('dddd D MMMM YYYY · HH:mm')}
        </Typography>
        <FlagStripe height={6} />
      </Box>
    </Box>
  );
};

const LoginInner: React.FC<{ brand: LoginBrand }> = ({ brand }) => {
  const { login, verifyOtp } = useUSAJILI();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const onSubmit = handleSubmit(async (values) => {
    setLoading(true);
    setErr(null);
    try {
      const ch = await login(values.email.trim(), values.password);
      if (ch) setChallenge(ch);
      else navigate(brand.home, { replace: true });
    } catch (e: any) {
      setErr(e?.message || e?.error?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  });

  const iconSx = { color: 'text.secondary', fontSize: 20 };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' },
        bgcolor: 'background.default',
      }}
    >
      <BrandPanel brand={brand} />

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2.5, sm: 4 },
          py: 6,
        }}
      >
        <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
          <USAJILIColorModeToggle />
        </Box>

        <Box
          sx={(t) => ({
            width: '100%',
            maxWidth: 420,
            bgcolor: 'background.paper',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow:
              t.palette.mode === 'dark'
                ? '0 20px 50px rgba(0,0,0,0.45)'
                : `0 20px 50px ${alpha('#0f1419', 0.12)}`,
          })}
        >
          <FlagStripe />
          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Compact brand header for small screens (brand panel is hidden there) */}
            <Stack alignItems="center" spacing={1} sx={{ display: { xs: 'flex', md: 'none' }, mb: 3 }}>
              <CoatOfArms size={64} />
              <Typography sx={{ fontWeight: 700, fontSize: '1.4rem', letterSpacing: 1, color: 'primary.main' }}>
                {brand.title}
              </Typography>
            </Stack>

            <Typography sx={{ fontWeight: 700, fontSize: '1.5rem', color: 'text.primary' }}>
              {challenge ? 'Verify sign-in' : 'Sign in'}
            </Typography>
            <Typography sx={{ mt: 0.5, mb: 3, fontSize: '0.9rem', color: 'text.secondary' }}>
              {challenge ? 'This account needs a one-time code.' : 'Use your SDICS administrator account to continue.'}
            </Typography>

            {challenge ? (
              <OtpCodeStep
                challenge={challenge}
                onVerify={async (code) => {
                  await verifyOtp(challenge.otp_token, code);
                  navigate(brand.home, { replace: true });
                }}
                onBack={() => setChallenge(null)}
              />
            ) : (
            <>

            {err && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>
                {err}
              </Alert>
            )}

            <form onSubmit={onSubmit} noValidate>
              <Stack spacing={2.25}>
                <TextField
                  label="Email address"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  fullWidth
                  {...register('email', { required: 'Email is required' })}
                  error={!!errors.email}
                  helperText={errors.email?.message as string | undefined}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutline sx={iconSx} />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Password"
                  autoComplete="current-password"
                  type={showPw ? 'text' : 'password'}
                  fullWidth
                  {...register('password', { required: 'Password is required' })}
                  error={!!errors.password}
                  helperText={errors.password?.message as string | undefined}
                  disabled={loading}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined sx={iconSx} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPw((s) => !s)}
                          edge="end"
                          aria-label={showPw ? 'Hide password' : 'Show password'}
                          sx={{ color: 'text.secondary' }}
                        >
                          {showPw ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button
                  fullWidth
                  type="submit"
                  size="large"
                  variant="contained"
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} color="inherit" /> : undefined}
                  sx={{ mt: 0.5, py: 1.4, fontWeight: 700, letterSpacing: 0.5 }}
                >
                  {loading ? 'Signing in…' : 'Sign in'}
                </Button>
              </Stack>
            </form>
            </>
            )}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3, color: 'text.secondary' }}>
              <ShieldOutlined sx={{ fontSize: 16 }} />
              <Typography sx={{ fontSize: '0.78rem' }}>
                Authorised personnel only. Sign-ins and changes are logged.
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const BrandedLoginPage: React.FC<{ brand: LoginBrand }> = ({ brand }) => (
  <USAJILIThemeProvider>
    <USAJILIProvider>
      <LoginInner brand={brand} />
    </USAJILIProvider>
  </USAJILIThemeProvider>
);

export const USAJILILoginPage: React.FC = () => <BrandedLoginPage brand={USAJILI_BRAND} />;

export default USAJILILoginPage;
